import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { didCancel, task } from 'ember-concurrency';
import type { SubscriptionAccessChange } from 'lumen/components/settings/users/modals/modal-subscription-access';
import type Userhotelrelationship from 'lumen/models/userhotelrelationship';
import type { BulkDeletePayload } from 'lumen/services/settings-user-subscription-access';
import { timeout } from 'lumen/utils/waiters';

interface SortState {
  isAscending: boolean;
  valuePath: string;
}

export default class HotelSettingsUsersSubscriptionAccessController extends Controller {
  queryParams = ['userId', 'userEmail'];

  @service declare router: Services['router'];
  @service
  declare settingsUserSubscriptionAccess: Services['settings-user-subscription-access'];

  @tracked currentPage = 0;
  @tracked relationships: Userhotelrelationship[] = [];
  @tracked searchQuery: string | null = null;
  @tracked searchText = '';
  @tracked sortBy: string | null = null;
  @tracked sortDirection: 'asc' | 'desc' = 'asc';
  @tracked totalResults = 0;
  userEmail = '';
  userId = '';

  debouncedSearchTask = task({ restartable: true }, async (value: string) => {
    await timeout();

    const trimmed = value.trim();

    this.searchQuery = trimmed || null;

    await this.reload();
  });

  loadMoreTask = task({ drop: true }, async () => {
    if (!this.hasMore) {
      return;
    }

    await this.loadPage(this.currentPage + 1);
  });

  get hasMore(): boolean {
    return this.relationships.length < this.totalResults;
  }

  get isLoading(): boolean {
    // Paging reports through isLoadingMore: swapping the table body for the
    // loading skeleton collapses the scroll container and jumps to the top.
    if (this.isLoadingMore) {
      return false;
    }

    return this.settingsUserSubscriptionAccess.loadRelationshipsTask.isRunning;
  }

  get isLoadingMore(): boolean {
    return this.loadMoreTask.isRunning;
  }

  get isOwnUser(): boolean {
    return this.settingsUserSubscriptionAccess.isOwnUser(this.userId);
  }

  get sorts(): SortState[] {
    if (!this.sortBy) {
      return [];
    }

    return [
      {
        valuePath: this.sortBy,
        isAscending: this.sortDirection === 'asc',
      },
    ];
  }

  private async loadPage(page: number): Promise<void> {
    const result =
      await this.settingsUserSubscriptionAccess.loadRelationshipsTask.perform({
        page,
        searchQuery: this.searchQuery ?? undefined,
        sortBy: this.sortBy ?? undefined,
        sortDirection: this.sortDirection,
        userId: this.userId,
      });

    if (!result) {
      this.totalResults = this.relationships.length;
      return;
    }

    this.currentPage = page;
    this.totalResults = result.totalResults;
    this.relationships =
      page === 0
        ? result.relationships
        : [...this.relationships, ...result.relationships];
  }

  private async reload(): Promise<void> {
    this.currentPage = 0;
    this.relationships = [];

    try {
      await this.loadPage(0);
    } catch (error) {
      // The load task is restartable, so it gets canceled whenever a new load
      // starts or the route is torn down. Only real errors are worth re-throwing.
      if (!didCancel(error)) {
        throw error;
      }
    }
  }

  async resetAndLoad(): Promise<void> {
    this.searchText = '';
    this.searchQuery = null;
    this.sortBy = null;
    this.sortDirection = 'asc';
    this.totalResults = 0;

    await this.reload();
  }

  @action
  async addSubscriptionAccess(
    subscriptionIds: number[],
    exclude: boolean,
  ): Promise<void> {
    const success =
      await this.settingsUserSubscriptionAccess.addSubscriptionAccess(
        this.userId,
        subscriptionIds,
        exclude,
      );

    if (success) {
      await this.reload();
    }
  }

  @action
  async bulkDelete(payload: BulkDeletePayload): Promise<boolean> {
    const success =
      await this.settingsUserSubscriptionAccess.bulkDelete(payload);

    this.router.refresh('hotel.settings.users.subscription-access');

    return success;
  }

  @action
  close(): void {
    this.router.transitionTo('hotel.settings.users');
  }

  @action
  async deleteRelationship(
    subscriptionId: number,
    subscriptionName: string,
  ): Promise<void> {
    const success =
      await this.settingsUserSubscriptionAccess.deleteRelationship(
        this.userId,
        this.userEmail,
        subscriptionId,
        subscriptionName,
      );

    if (success) {
      this.relationships = this.relationships.filter(
        (relationship) => relationship.subscriptionId !== subscriptionId,
      );
      this.totalResults = Math.max(0, this.totalResults - 1);
    } else {
      await this.reload();
    }
  }

  @action
  onLoadMore(): void {
    void this.loadMoreTask.perform();
  }

  @action
  onSearchChange(event: Event): void {
    this.searchText = (event.target as HTMLInputElement).value;
    void this.debouncedSearchTask.perform(this.searchText);
  }

  @action
  onSortChange(sorts: SortState[]): void {
    if (sorts[0]) {
      this.sortBy = sorts[0].valuePath;
      this.sortDirection = sorts[0].isAscending ? 'asc' : 'desc';
    }

    void this.reload();
  }

  @action
  async saveChanges(changes: SubscriptionAccessChange[]): Promise<boolean> {
    const success = await this.settingsUserSubscriptionAccess.saveChanges(
      this.userId,
      changes,
    );

    await this.reload();

    return success;
  }
}
